'use client'

import {
  createContext,
  useContext,
  useMemo,
  type CSSProperties,
  type PropsWithChildren,
} from 'react'

/**
 * Theme tokens consumed by `<BrandingProvider>`. Shape matches
 * `BrandingThemeRuntimeConfig` from @cwflow/flow-config but the package
 * stays free of that dep so the widget bundle can pull only flow-ui.
 */
export interface BrandingThemeColors {
  primary?: string
  primaryLight?: string
  primaryDark?: string
  secondary?: string
  accent?: string
  accentLight?: string
  text?: string
  background?: string
  surface?: string
  // Extended palette — additive, all optional. Each key maps to a single
  // CSS variable; missing keys fall through to the cwFLOW default in
  // globals.css.
  muted?: string
  success?: string
  warning?: string
  danger?: string
  // Foreground (text/icon) colour to use on top of each filled brand
  // surface. Defaults are white on primary/accent + near-black on
  // secondary, suitable for most brand palettes. Operators only need to
  // override when their colour clashes with the default contrast (e.g.
  // a light primary → set on_primary to a dark colour).
  on_primary?: string
  on_secondary?: string
  on_accent?: string
}

export interface BrandingThemeFonts {
  heading?: string
  body?: string
}

/**
 * Per-surface border-radius tokens. Values are CSS length strings
 * (`"16px"`, `"9999px"` for a pill, `"0"` for square). The four keys
 * cover ~95% of cwFLOW's visible roundness; further surfaces inherit
 * one of these (e.g. option-cards reuse --cw-radius-input).
 */
export interface BrandingThemeRadius {
  button?: string
  card?: string
  input?: string
  badge?: string
}

/**
 * Layout overrides. Currently just header logo height — operators like
 * to size their logo to fit different visual densities. Maps to the
 * --cw-logo-height CSS variable.
 */
export interface BrandingThemeLayout {
  logo_height?: string
}

export interface BrandingTheme {
  colors?: BrandingThemeColors
  fonts?: BrandingThemeFonts
  radius?: BrandingThemeRadius
  layout?: BrandingThemeLayout
}

export interface BrandingContextValue {
  theme: BrandingTheme
  cssVariables: Record<string, string>
  rootClassName: string
}

const BrandingContext = createContext<BrandingContextValue | null>(null)

const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i

function hexToRgb(input: string): string | null {
  if (!input) return null
  const match = input.trim().match(HEX_RE)
  if (!match) return null
  let hex = match[1]
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('')
  }
  const r = parseInt(hex.slice(0, 2), 16)
  const g = parseInt(hex.slice(2, 4), 16)
  const b = parseInt(hex.slice(4, 6), 16)
  return `${r} ${g} ${b}`
}

function setIfDefined(target: Record<string, string>, key: string, value?: string) {
  if (value && typeof value === 'string') {
    target[key] = value
  }
}

export function buildBrandingCssVariables(theme: BrandingTheme | undefined): Record<string, string> {
  const vars: Record<string, string> = {}
  const colors = theme?.colors
  if (colors) {
    setIfDefined(vars, '--cw-color-primary', colors.primary)
    setIfDefined(vars, '--cw-color-primary-light', colors.primaryLight)
    setIfDefined(vars, '--cw-color-primary-dark', colors.primaryDark)
    setIfDefined(vars, '--cw-color-secondary', colors.secondary)
    setIfDefined(vars, '--cw-color-accent', colors.accent)
    setIfDefined(vars, '--cw-color-accent-light', colors.accentLight)
    setIfDefined(vars, '--cw-color-text', colors.text)
    setIfDefined(vars, '--cw-color-background', colors.background)
    setIfDefined(vars, '--cw-color-surface', colors.surface)
    // Extended palette
    setIfDefined(vars, '--cw-color-muted', colors.muted)
    setIfDefined(vars, '--cw-color-success', colors.success)
    setIfDefined(vars, '--cw-color-warning', colors.warning)
    setIfDefined(vars, '--cw-color-danger', colors.danger)
    // On-* foreground colours for filled brand surfaces. Optional —
    // sensible defaults live in globals.css and are usually fine.
    setIfDefined(vars, '--cw-color-on-primary', colors.on_primary)
    setIfDefined(vars, '--cw-color-on-secondary', colors.on_secondary)
    setIfDefined(vars, '--cw-color-on-accent', colors.on_accent)

    const rgbMap: Array<[string, string | undefined]> = [
      ['--cw-color-primary-rgb', colors.primary],
      ['--cw-color-primary-light-rgb', colors.primaryLight],
      ['--cw-color-primary-dark-rgb', colors.primaryDark],
      ['--cw-color-secondary-rgb', colors.secondary],
      ['--cw-color-accent-rgb', colors.accent],
      ['--cw-color-accent-light-rgb', colors.accentLight],
      ['--cw-color-text-rgb', colors.text],
      ['--cw-color-background-rgb', colors.background],
      ['--cw-color-surface-rgb', colors.surface],
    ]

    for (const [varName, value] of rgbMap) {
      if (!value) continue
      const rgb = hexToRgb(value)
      if (rgb) {
        vars[varName] = rgb
      }
    }
  }

  const fonts = theme?.fonts
  if (fonts) {
    setIfDefined(vars, '--cw-font-heading', fonts.heading)
    setIfDefined(vars, '--cw-font-body', fonts.body)
  }

  // Radius tokens. Values are passed through unchanged — validation is
  // already done at the centrum API boundary, and a malformed value here
  // would just make the component fall back to its globals.css default.
  const radius = theme?.radius
  if (radius) {
    setIfDefined(vars, '--cw-radius-button', radius.button)
    setIfDefined(vars, '--cw-radius-card', radius.card)
    setIfDefined(vars, '--cw-radius-input', radius.input)
    setIfDefined(vars, '--cw-radius-badge', radius.badge)
  }

  // Layout. logo_height is read by Header.tsx as the inline height of
  // the <img>; falls back to 2.5rem (the previous fixed h-10 class).
  const layout = theme?.layout
  if (layout) {
    setIfDefined(vars, '--cw-logo-height', layout.logo_height)
  }

  return vars
}

export interface BrandingProviderProps extends PropsWithChildren {
  theme?: BrandingTheme
  /** Element used as scope. Defaults to a div with class `cwflow-root`. */
  as?: 'div' | 'section' | 'article'
  className?: string
  /**
   * When true, additionally writes the CSS variables to `:root` via a
   * `<style>` tag so global utilities pick them up. Use for hosted/iframe
   * mode; leave off for the widget mount, where isolation matters more.
   */
  applyToDocumentRoot?: boolean
  /** Optional id for the scoped style element (helpful in tests). */
  styleId?: string
}

export function BrandingProvider({
  theme,
  children,
  as: Tag = 'div',
  className,
  applyToDocumentRoot = false,
  styleId,
}: BrandingProviderProps) {
  const cssVariables = useMemo(() => buildBrandingCssVariables(theme), [theme])

  const inlineStyle = useMemo<CSSProperties>(() => {
    return cssVariables as unknown as CSSProperties
  }, [cssVariables])

  const documentRootStyle = useMemo(() => {
    if (!applyToDocumentRoot) return null
    const lines = Object.entries(cssVariables)
      .map(([key, value]) => `  ${key}: ${value};`)
      .join('\n')
    if (!lines) return null
    return `:root, .cwflow-root {\n${lines}\n}`
  }, [applyToDocumentRoot, cssVariables])

  const value = useMemo<BrandingContextValue>(
    () => ({
      theme: theme || {},
      cssVariables,
      rootClassName: 'cwflow-root',
    }),
    [theme, cssVariables]
  )

  return (
    <BrandingContext.Provider value={value}>
      {documentRootStyle && (
        <style id={styleId || 'cwflow-branding-vars'}>{documentRootStyle}</style>
      )}
      <Tag className={['cwflow-root', className].filter(Boolean).join(' ')} style={inlineStyle}>
        {children}
      </Tag>
    </BrandingContext.Provider>
  )
}

export function useBranding(): BrandingContextValue {
  const ctx = useContext(BrandingContext)
  if (!ctx) {
    return {
      theme: {},
      cssVariables: {},
      rootClassName: 'cwflow-root',
    }
  }
  return ctx
}
